import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '@codes/ui-kit/components/ui/button';
import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { AgentView, PromptView } from '@lemoncloud/ssocio-chatbots-api';
import { GenAIResponse } from '@lemoncloud/ssocio-chatbots-api/dist/lib/openai/openai-types';
import { useQueryClient } from '@tanstack/react-query';
import { EMBEDDING_OPTIONS, TAB_CONFIG } from '@web/app/features/agents/consts';
import { PromptState } from '@web/app/features/agents/types';
import {
    isFromTemplateHook,
    isTemplatePrompt,
    requestCreateAgent,
    requestUpdateAgent,
    updateCacheWithNewPrompt,
    updateCacheWithUpdatedPrompt,
} from '@web/app/features/agents/utils';
import axios from 'axios';
import { Loader2 } from 'lucide-react';

import {
    useAgent,
    useClonePrompt,
    useCreateAgent,
    useCreatePrompt,
    useGenerateAIResponse,
    usePrompt,
    usePrompts,
    useRevisePrompt,
    useTemplatePrompts,
    useUpdateAgent,
} from '@codes/agents';
import { AgentFormData, toSelectOptions, viewToFormData } from '@codes/agents';
import { useBrains } from '@codes/chatbots';

import { AgentFormBasic, AgentFormBasicRef } from './AgentFormBasic';
import { AgentFormPersona } from './AgentFormPersona';
import { ResponseTestDialog } from './ResponseTestDialog';

interface AgentFormProps {
    mode: 'create' | 'edit';
    agentId: string | null;
    agentData?: AgentView | null;
    onFormSuccess: (agentId: string) => void;
    onChatClick?: (agentId: string) => void;
    isCreatingChat?: boolean;
    className?: string;
}

export const AgentForm = ({
    mode,
    agentData,
    agentId,
    onFormSuccess,
    onChatClick,
    isCreatingChat = false,
    className,
}: AgentFormProps) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    // State
    const [activeTab, setActiveTab] = useState('basic');
    const [formData, setFormData] = useState<AgentFormData>({});
    const [systemPromptState, setSystemPromptState] = useState<PromptState>({
        mode: 'existing',
        customContent: '',
        loadedContent: '',
        originalContent: '',
    });
    const [userPromptState, setUserPromptState] = useState<PromptState>({
        mode: 'existing',
        customContent: '',
        loadedContent: '',
        originalContent: '',
    });
    const [isResponseTestOpen, setIsResponseTestOpen] = useState(false);
    const [testInput, setTestInput] = useState('');
    const [testResponse, setTestResponse] = useState<GenAIResponse | null>(null);
    const [testError, setTestError] = useState<string | null>(null);
    const [newlyCreatedPrompts, setNewlyCreatedPrompts] = useState<{
        systemPrompts: PromptView[];
        userPrompts: PromptView[];
    }>({ systemPrompts: [], userPrompts: [] });

    const advancedFormRef = useRef<AgentFormBasicRef>(null);

    // Data fetching
    const { data: fallbackAgent, isLoading: isLoadingAgent } = useAgent(
        agentId || '',
        mode === 'edit' && Boolean(agentId) && !agentData
    );

    const { data: brainsData, isLoading: isLoadingBrains, error: brainsError } = useBrains({});

    // Template prompts
    const {
        data: systemTemplateData,
        isLoading: isLoadingSystemTemplates,
        error: systemTemplatesError,
        refetch: systemTemplatesRefetch,
    } = useTemplatePrompts({ type: 'system' });

    const {
        data: userTemplateData,
        isLoading: isLoadingUserTemplates,
        error: userTemplatesError,
        refetch: userTemplatesRefetch,
    } = useTemplatePrompts({ type: 'user' });

    const {
        data: systemUserPromptsData,
        isLoading: isLoadingSystemUserPrompts,
        error: systemUserPromptsError,
        refetch: systemUserPromptsRefetch,
    } = usePrompts({ parentId: '', stereo: 'system', view: 'mine', limit: -1 });

    const {
        data: userUserPromptsData,
        isLoading: isLoadingUserUserPrompts,
        error: userUserPromptsError,
        refetch: userUserPromptsRefetch,
    } = usePrompts({ parentId: '', stereo: 'user', view: 'mine', limit: -1 });

    // Combined prompt data with duplicate removal
    const systemPromptData = useMemo(() => {
        const templateData = systemTemplateData?.data || [];
        const userData = systemUserPromptsData?.data || [];

        // 중복 제거: 사용자 프롬프트가 템플릿과 같은 ID를 가진 경우 사용자 프롬프트 우선
        const uniqueData = [...templateData];
        userData.forEach(userPrompt => {
            const existingIndex = uniqueData.findIndex(template => template.id === userPrompt.id);
            if (existingIndex >= 0) {
                uniqueData[existingIndex] = userPrompt; // 사용자 프롬프트로 교체
            } else {
                uniqueData.push(userPrompt);
            }
        });

        return {
            data: uniqueData,
            total: uniqueData.length,
        };
    }, [systemTemplateData, systemUserPromptsData]);

    const userPromptsData = useMemo(() => {
        const templateData = userTemplateData?.data || [];
        const userData = userUserPromptsData?.data || [];

        // 중복 제거: 사용자 프롬프트가 템플릿과 같은 ID를 가진 경우 사용자 프롬프트 우선
        const uniqueData = [...templateData];
        userData.forEach(userPrompt => {
            const existingIndex = uniqueData.findIndex(template => template.id === userPrompt.id);
            if (existingIndex >= 0) {
                uniqueData[existingIndex] = userPrompt; // 사용자 프롬프트로 교체
            } else {
                uniqueData.push(userPrompt);
            }
        });

        return {
            data: uniqueData,
            total: uniqueData.length,
        };
    }, [userTemplateData, userUserPromptsData]);

    // Current prompts for edit mode
    const { data: currentSystemPrompt } = usePrompt(mode === 'edit' && agentData?.promptId ? agentData.promptId : '');
    const { data: currentUserPrompt } = usePrompt(
        mode === 'edit' && agentData?.userPromptId ? agentData.userPromptId : ''
    );

    // Mutations
    const createMutation = useCreateAgent();
    const updateMutation = useUpdateAgent();
    const createPromptMutation = useCreatePrompt();
    const clonePromptMutation = useClonePrompt();
    const revisePromptMutation = useRevisePrompt();
    const generateResponseMutation = useGenerateAIResponse();

    // Computed values
    const currentAgent = useMemo(() => {
        return agentData || fallbackAgent || null;
    }, [agentData, fallbackAgent]);

    const enhancedPromptsData = useMemo(() => {
        const systemPrompts = [...(systemPromptData?.data || [])];
        const userPrompts = [...(userPromptsData?.data || [])];

        // 편집 모드에서 현재 프롬프트가 목록에 없으면 추가
        if (mode === 'edit' && currentSystemPrompt && currentSystemPrompt.id) {
            const existsInList = systemPrompts.some(p => p.id === currentSystemPrompt.id);
            if (!existsInList) {
                systemPrompts.push(currentSystemPrompt);
            }
        }

        if (mode === 'edit' && currentUserPrompt && currentUserPrompt.id) {
            const existsInList = userPrompts.some(p => p.id === currentUserPrompt.id);
            if (!existsInList) {
                userPrompts.push(currentUserPrompt);
            }
        }

        newlyCreatedPrompts.systemPrompts.forEach(prompt => {
            const existsInList = systemPrompts.some(p => p.id === prompt.id);
            if (!existsInList && prompt.id) {
                systemPrompts.unshift(prompt); // 맨 앞에 추가
            }
        });

        newlyCreatedPrompts.userPrompts.forEach(prompt => {
            const existsInList = userPrompts.some(p => p.id === prompt.id);
            if (!existsInList && prompt.id) {
                userPrompts.unshift(prompt); // 맨 앞에 추가
            }
        });

        const uniqueSystemPrompts = systemPrompts.filter(
            (prompt, index, self) => prompt.id && self.findIndex(p => p.id === prompt.id) === index
        );
        const uniqueUserPrompts = userPrompts.filter(
            (prompt, index, self) => prompt.id && self.findIndex(p => p.id === prompt.id) === index
        );

        return {
            systemPrompts: uniqueSystemPrompts,
            userPrompts: uniqueUserPrompts,
        };
    }, [
        systemPromptData?.data,
        userPromptsData?.data,
        mode,
        currentSystemPrompt,
        currentUserPrompt,
        newlyCreatedPrompts,
    ]);

    const selectOptions = useMemo(
        () => ({
            embeddings: toSelectOptions.embeddings([...EMBEDDING_OPTIONS]),
            brains: toSelectOptions.brains(brainsData?.data || []),
            systemPrompts: toSelectOptions.prompts(enhancedPromptsData.systemPrompts),
            userPrompts: toSelectOptions.prompts(enhancedPromptsData.userPrompts),
        }),
        [brainsData, enhancedPromptsData.systemPrompts, enhancedPromptsData.userPrompts]
    );

    const getDefaultFormData = (): AgentFormData => ({
        temperature: 100,
        maxTokens: 2048,
        brainId: selectOptions.brains[0]?.value || '',
        embedding: selectOptions.embeddings[0]?.value || '',
        promptId: selectOptions.systemPrompts[0]?.value || '',
        userPromptId: selectOptions.userPrompts[0]?.value || '',
    });

    // Loading states
    const isDataLoading =
        isLoadingBrains ||
        isLoadingSystemTemplates ||
        isLoadingUserTemplates ||
        isLoadingSystemUserPrompts ||
        isLoadingUserUserPrompts;
    const isSaving =
        createMutation.isPending ||
        updateMutation.isPending ||
        createPromptMutation.isPending ||
        clonePromptMutation.isPending ||
        revisePromptMutation.isPending;
    const isAgentLoading = mode === 'edit' && Boolean(agentId) && !agentData && isLoadingAgent;
    const isLoading = isSaving || isAgentLoading;
    const isChatButtonLoading = isCreatingChat;

    const isFormValid = formData.nick?.trim() && formData.brainId && formData.embedding;

    // Effects
    useEffect(() => {
        if (mode === 'edit' && currentAgent) {
            setFormData(viewToFormData(currentAgent));
        } else if (mode === 'create' && selectOptions.brains.length > 0) {
            setFormData(getDefaultFormData());
        }
    }, [mode, currentAgent, selectOptions.brains.length]);

    const handleFormDataChange = (updates: Partial<AgentFormData>) => {
        setFormData(prev => ({ ...prev, ...updates }));
    };

    const handleSystemPromptStateChange = useCallback((updates: Partial<PromptState>) => {
        setSystemPromptState(prev => ({ ...prev, ...updates }));
    }, []);

    const handleUserPromptStateChange = useCallback((updates: Partial<PromptState>) => {
        setUserPromptState(prev => ({ ...prev, ...updates }));
    }, []);

    const handleChat = () => {
        if (agentId && mode === 'edit' && onChatClick) {
            onChatClick(agentId);
        }
    };

    const handleResponseTest = async () => {
        if (!agentId || mode !== 'edit') {
            toast({
                title: t('agent.form.responseTest.error.noAgent', '에이전트를 먼저 저장해주세요'),
                variant: 'destructive',
            });
            return;
        }

        setTestResponse(null);
        setTestError(null);
        setIsResponseTestOpen(true);
    };

    const handleSendTest = async () => {
        if (!agentId || mode !== 'edit') {
            return;
        }

        try {
            setTestResponse(null);
            setTestError(null);

            const response = await generateResponseMutation.mutateAsync({
                agentId,
                input: testInput || t('agent.form.responseTest.defaultInput'),
            });

            setTestResponse(response);
        } catch (error: unknown) {
            console.error('Response test error:', error);
            const errorMessage = axios.isAxiosError(error)
                ? error.response?.data || error.message
                : error instanceof Error
                ? error.message
                : 'An error occurred while sending the message';
            setTestError(`ERROR: ${errorMessage}`);
        }
    };

    // Prompt handling helpers (extracted from handleTemplatePromptCreation)
    const handleTemplateCreate = async (
        promptData: { name: string; contents: string[]; stereo: string },
        type: 'system' | 'user'
    ): Promise<string | undefined> => {
        const createdPrompt = await createPromptMutation.mutateAsync(promptData);
        if (!createdPrompt.id) {
            return undefined;
        }

        updateCacheWithNewPrompt(queryClient, 'prompts', createdPrompt);
        const key = type === 'system' ? 'systemPrompts' : 'userPrompts';
        setNewlyCreatedPrompts(prev => ({ ...prev, [key]: [createdPrompt, ...prev[key]] }));
        return createdPrompt.id;
    };

    const handleTemplateClone = async (
        promptState: PromptState,
        promptData: { name: string; contents: string[]; stereo: string },
        type: 'system' | 'user'
    ): Promise<string | undefined> => {
        if (!promptState.selectedPromptId) {
            return undefined;
        }

        const clonedPrompt = await clonePromptMutation.mutateAsync({
            promptId: promptState.selectedPromptId,
            ...promptData,
        });
        if (!clonedPrompt.$child?.id) {
            return undefined;
        }

        updateCacheWithNewPrompt(queryClient, 'prompts', clonedPrompt.$child);
        const key = type === 'system' ? 'systemPrompts' : 'userPrompts';
        setNewlyCreatedPrompts(prev => ({ ...prev, [key]: [clonedPrompt.$child, ...prev[key]] }));
        return clonedPrompt.$child.id;
    };

    const handleTemplateRevise = async (
        promptState: PromptState,
        contents: string[],
        type: 'system' | 'user'
    ): Promise<string | undefined> => {
        if (!promptState.selectedPromptId) {
            return undefined;
        }

        const revisedPrompt = await revisePromptMutation.mutateAsync({
            promptId: promptState.selectedPromptId,
            contents,
        });
        if (!revisedPrompt.$parent?.id) {
            return undefined;
        }

        updateCacheWithUpdatedPrompt(queryClient, 'prompts', revisedPrompt.$child, promptState.selectedPromptId);
        if (revisedPrompt.$child) {
            const key = type === 'system' ? 'systemPrompts' : 'userPrompts';
            setNewlyCreatedPrompts(prev => ({ ...prev, [key]: [revisedPrompt.$child, ...prev[key]] }));
        }
        return revisedPrompt.$parent.id;
    };

    const handleSinglePromptCreation = async (
        type: 'system' | 'user',
        promptState: PromptState,
        templateData: { name: string; contents: string[]; stereo: string } | undefined
    ): Promise<string | undefined> => {
        if (!promptState.selectedPromptId) {
            return undefined;
        }

        const prompts = type === 'system' ? enhancedPromptsData.systemPrompts : enhancedPromptsData.userPrompts;
        const selectedPrompt = prompts.find(p => p.id === promptState.selectedPromptId);
        const isTemplate = selectedPrompt ? isTemplatePrompt(selectedPrompt) : false;

        if (isTemplate) {
            const hasModifiedContent = promptState.loadedContent !== promptState.originalContent;
            const contents = hasModifiedContent
                ? promptState.loadedContent?.split('\n') || []
                : selectedPrompt?.contents || [];

            const defaultLabel =
                type === 'system'
                    ? t('agent.form.advanced.prompts.systemPrompt')
                    : t('agent.form.advanced.prompts.userPrompt');
            const promptData = templateData || {
                name: selectedPrompt?.name || defaultLabel,
                contents,
                stereo: type,
            };

            if (isFromTemplateHook(selectedPrompt)) {
                return handleTemplateCreate(promptData, type);
            }
            return handleTemplateClone(promptState, promptData, type);
        }

        if (templateData) {
            return handleTemplateRevise(promptState, templateData.contents, type);
        }
        return undefined;
    };

    const handleTemplatePromptCreation = async () => {
        const promptTemplateData = advancedFormRef.current?.getPromptTemplateData();

        const promptId = await handleSinglePromptCreation(
            'system',
            systemPromptState,
            promptTemplateData?.systemPrompt
        );
        const userPromptId = await handleSinglePromptCreation('user', userPromptState, promptTemplateData?.userPrompt);

        const promptUpdates: { promptId?: string; userPromptId?: string } = {};
        if (promptId) {
            promptUpdates.promptId = promptId;
        }
        if (userPromptId) {
            promptUpdates.userPromptId = userPromptId;
        }
        return promptUpdates;
    };

    const handleSave = async () => {
        try {
            // Step 1: Handle template prompt operations
            const templateUpdates = await handleTemplatePromptCreation();

            // Step 2: Use template updates
            const finalFormData = { ...formData, ...templateUpdates };

            // Step 3: Execute agent operation
            const result = await (mode === 'create'
                ? requestCreateAgent(finalFormData, createMutation, queryClient, t)
                : mode === 'edit' && agentId && typeof agentId === 'string'
                ? requestUpdateAgent(agentId, finalFormData, updateMutation, queryClient, t)
                : Promise.reject(new Error('Invalid operation state')));

            // Step 4: Cleanup and notify success
            if (result.id) {
                // 즉시 성공 콜백 호출
                onFormSuccess(result.id);

                // 약간의 지연 후 상태 정리 및 캐시 갱신
                setTimeout(() => {
                    // 저장 성공 후 새로 생성된 프롬프트 state 초기화
                    setNewlyCreatedPrompts({ systemPrompts: [], userPrompts: [] });

                    // 프롬프트 상태 리셋 (템플릿 상태 해제)
                    if (finalFormData.promptId) {
                        setSystemPromptState(prev => ({
                            ...prev,
                            mode: 'existing',
                            isTemplate: false,
                            selectedPromptId: finalFormData.promptId,
                        }));
                    }

                    if (finalFormData.userPromptId) {
                        setUserPromptState(prev => ({
                            ...prev,
                            mode: 'existing',
                            isTemplate: false,
                            selectedPromptId: finalFormData.userPromptId,
                        }));
                    }

                    // 프롬프트 캐시 갱신
                    systemTemplatesRefetch?.();
                    userTemplatesRefetch?.();
                    systemUserPromptsRefetch?.();
                    userUserPromptsRefetch?.();
                }, 100);
            }
        } catch (error) {
            console.error('Error in handleSave:', error);
            toast({
                title: t('agent.form.messages.saveError'),
                variant: 'destructive',
            });
        }
    };

    // Render functions
    const renderTabs = () => (
        <div className="flex items-center gap-[27px]">
            {TAB_CONFIG.map(({ key, labelKey }) => (
                <button
                    key={key}
                    className={`relative text-xs after:content-[''] after:absolute after:bottom-[-3px] after:left-1/2 after:-translate-x-1/2 after:w-[18px] after:h-[2px] after:rounded-full transition-colors ${
                        activeTab === key ? 'text-text-800 after:bg-text-800' : 'text-text-400 after:bg-transparent'
                    }`}
                    onClick={() => setActiveTab(key)}
                >
                    {t(labelKey)}
                </button>
            ))}
        </div>
    );

    const renderActions = () => (
        <div className="flex items-center gap-2">
            {mode === 'edit' && agentId && (
                <>
                    <Button
                        size="sm"
                        variant="outline"
                        className="h-8 text-xs border-point text-point hover:bg-point hover:text-white transition-all duration-200"
                        onClick={handleResponseTest}
                        disabled={isLoading || generateResponseMutation.isPending}
                    >
                        {generateResponseMutation.isPending ? (
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        ) : (
                            t('agent.form.actions.responseTest', '응답테스트')
                        )}
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        className="h-8 text-xs border-point text-point hover:bg-point hover:text-white transition-all duration-200"
                        onClick={handleChat}
                        disabled={isLoading || isChatButtonLoading}
                    >
                        {isChatButtonLoading ? (
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        ) : (
                            t('agent.form.actions.chat')
                        )}
                    </Button>
                </>
            )}
            <Button
                size="sm"
                className="w-[52px] h-8 text-xs"
                onClick={handleSave}
                disabled={isLoading || !isFormValid || isDataLoading}
            >
                {isSaving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                    t(mode === 'create' ? 'agent.form.actions.create' : 'agent.form.actions.save')
                )}
            </Button>
        </div>
    );

    const renderContent = () => {
        if (isDataLoading) {
            return (
                <div className="flex items-center gap-2 w-full h-full justify-center">
                    <Loader2 className="h-5 w-5 animate-spin" />
                </div>
            );
        }

        return (
            <div className="flex-1 overflow-hidden">
                {activeTab === 'basic' && (
                    <AgentFormBasic
                        ref={advancedFormRef}
                        formData={formData}
                        onChange={handleFormDataChange}
                        isLoading={isLoading}
                        selectOptions={{
                            systemPrompts: selectOptions.systemPrompts,
                            userPrompts: selectOptions.userPrompts,
                            brains: selectOptions.brains,
                            embeddings: selectOptions.embeddings,
                        }}
                        promptsData={enhancedPromptsData}
                        onPromptClone={clonePromptMutation.mutateAsync}
                        onPromptUpdate={revisePromptMutation.mutateAsync}
                        systemPromptState={systemPromptState}
                        userPromptState={userPromptState}
                        onSystemPromptStateChange={handleSystemPromptStateChange}
                        onUserPromptStateChange={handleUserPromptStateChange}
                        hasBrainsError={!!brainsError}
                        hasEmbeddingsError={false}
                        systemPromptsError={systemTemplatesError || systemUserPromptsError}
                        userPromptsError={userTemplatesError || userUserPromptsError}
                        systemPromptsRefetch={systemTemplatesRefetch || systemUserPromptsRefetch}
                        userPromptsRefetch={userTemplatesRefetch || userUserPromptsRefetch}
                        isLoadingSystemPrompts={isLoadingSystemTemplates || isLoadingSystemUserPrompts}
                        isLoadingUserPrompts={isLoadingUserTemplates || isLoadingUserUserPrompts}
                        mode={mode}
                        currentSystemPrompt={currentSystemPrompt || null}
                        currentUserPrompt={currentUserPrompt || null}
                    />
                )}
                {activeTab === 'persona' && (
                    <AgentFormPersona formData={formData} onChange={handleFormDataChange} isLoading={isLoading} />
                )}
                {activeTab === 'api' && (
                    <div className="py-[52px] text-center dark:text-text-500">
                        {t('agent.form.placeholders.apiIntegration')}
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <div
                className={`w-full max-w-[992px] h-full bg-card shadow-custom border border-chatbot-line rounded-2xl flex flex-col max-md:h-full ${className}`}
            >
                <div className="flex flex-wrap gap-2 items-center justify-between py-3.5 px-4">
                    <div className="text-sm font-medium">{t('agent.form.title')}</div>
                    {renderActions()}
                </div>
                <div className="px-4 pb-[6px]">{renderTabs()}</div>
                {renderContent()}
            </div>

            <ResponseTestDialog
                isOpen={isResponseTestOpen}
                onOpenChange={setIsResponseTestOpen}
                response={testResponse}
                isLoading={generateResponseMutation.isPending}
                testInput={testInput}
                onTestInputChange={setTestInput}
                onSendTest={handleSendTest}
                error={testError}
            />
        </>
    );
};
